import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

let scriptPromise = null;
const loadScript = (domain) => {
  if (window.JitsiMeetExternalAPI) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://${domain}/external_api.js`;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => { scriptPromise = null; reject(new Error("jitsi script failed")); };
    document.body.appendChild(s);
  });
  return scriptPromise;
};

// Jitsi Meet embed through our provider abstraction. Recording controls are excluded from the
// toolbar by default (recording stays OFF unless enabled + reviewed at the provider level).
export default function JitsiRoom({ video, onLeave, height = 460 }) {
  const ref = useRef(null);
  const apiRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadScript(video.domain).then(() => {
      if (cancelled || !ref.current || !window.JitsiMeetExternalAPI) { if (!window.JitsiMeetExternalAPI) setErr(true); return; }
      const toolbar = ["microphone", "camera", "hangup", "chat", "tileview", "fullscreen", "settings", "raisehand", "desktop"];
      apiRef.current = new window.JitsiMeetExternalAPI(video.domain, {
        roomName: video.room_name,
        parentNode: ref.current,
        userInfo: { displayName: video.display_name || "Guest" },
        configOverwrite: {
          prejoinPageEnabled: false, disableDeepLinking: true, startWithAudioMuted: true,
          fileRecordingsEnabled: !!video.recording_enabled, liveStreamingEnabled: false,
        },
        interfaceConfigOverwrite: { TOOLBAR_BUTTONS: toolbar, SHOW_JITSI_WATERMARK: false, MOBILE_APP_PROMO: false },
      });
      apiRef.current.addEventListener("videoConferenceLeft", () => onLeave && onLeave());
      setLoading(false);
    }).catch(() => { setErr(true); setLoading(false); });
    return () => { cancelled = true; try { apiRef.current?.dispose(); } catch { /* noop */ } };
  }, []); // eslint-disable-line

  if (err) return (
    <div className="p-6 text-center text-white/80 bg-[#0f2733] rounded-xl" data-testid="jitsi-error">
      Couldn't load the video here. Open it directly: <a className="underline" href={video.join_url} target="_blank" rel="noreferrer">{video.join_url}</a>
    </div>
  );
  return (
    <div className="relative w-full rounded-xl overflow-hidden bg-[#0f2733]" style={{ height }} data-testid="jitsi-room">
      {loading && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="animate-spin text-white" size={26} /></div>}
      <div ref={ref} className="w-full h-full" />
    </div>
  );
}
